import { useEffect, useRef, useState, type KeyboardEvent, type TextareaHTMLAttributes, type InputHTMLAttributes } from 'react'

/**
 * A single-line text input that keeps a local draft while the user is typing
 * and commits the value to the parent on blur or Enter (not on every keystroke).
 * Use this for fields backed by undo history so each edit becomes one undo step.
 *
 * Re-seeds from `value` whenever the committed upstream value changes (e.g. after
 * undo or re-bake) so the field stays in sync.
 */
export interface CommitInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown'> {
  value: string
  onCommit: (next: string) => void
}

export function CommitInput({ value, onCommit, ...rest }: CommitInputProps) {
  const [draft, setDraft] = useState(value)
  // Track whether we already committed on Enter so blur doesn't double-fire.
  const committedRef = useRef(false)

  useEffect(() => {
    setDraft(value)
    committedRef.current = false
  }, [value])

  const commit = (next: string) => {
    if (committedRef.current) return
    committedRef.current = true
    if (next !== value) onCommit(next)
  }

  return (
    <input
      {...rest}
      value={draft}
      onChange={e => {
        committedRef.current = false
        setDraft(e.target.value)
      }}
      onBlur={() => commit(draft)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          commit(draft)
          e.currentTarget.blur()
        }
      }}
    />
  )
}

/**
 * A textarea variant of CommitInput. Used exclusively for SINGLE-LINE text
 * fields (the pipeline's maxLines=1 contract). Enter commits (does NOT insert a
 * newline — single-line semantics). Values are normalised on commit: trailing
 * CR/LF stripped so a stray newline can never leak into ^FD…^FS ZPL output.
 */
export interface CommitTextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown'> {
  value: string
  onCommit: (next: string) => void
}

export function CommitTextarea({ value, onCommit, ...rest }: CommitTextareaProps) {
  const [draft, setDraft] = useState(value)
  // Track whether we already committed on Enter so blur doesn't double-fire.
  const committedRef = useRef(false)

  useEffect(() => {
    setDraft(value)
    committedRef.current = false
  }, [value])

  // Normalise: strip trailing newlines/CRs. Pipeline is single-line (maxLines=1)
  // so any newline in ^FD is junk and should never be persisted.
  const normalise = (s: string) => s.replace(/[\r\n]+$/, '')

  const commit = (next: string) => {
    if (committedRef.current) return
    committedRef.current = true
    const normalised = normalise(next)
    if (normalised !== value) onCommit(normalised)
  }

  return (
    <textarea
      {...rest}
      value={draft}
      onChange={e => {
        committedRef.current = false
        setDraft(e.target.value)
      }}
      onBlur={() => commit(draft)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          // Single-line semantics: Enter commits, does NOT insert a newline.
          e.preventDefault()
          commit(draft)
          e.currentTarget.blur()
        }
      }}
    />
  )
}
